import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, useToast } from "@signet/ui";
import { api, type NowCardData } from "@signet/web-kit";
import { ago } from "../shared/format.ts";
import { BASE, keys } from "./api.ts";
import NoteBody from "./note-body.tsx";
import { CLink, creatorHref, messageOf } from "./shared.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Creators' cards on the Now screen: notes that name you, and onboarding
   nobody has finished. The Now page draws each card as it is, so each brings
   its own panel and heading, the same frame the core cards use.
   ────────────────────────────────────────────────────────────────────────── */

type NowPing = { noteId: string; creator: string; author: string; createdAt: string; body: string };

export function PingsNowCard({ card }: { card: NowCardData }) {
  const data = card.data as { total: number; pings: NowPing[] };
  const client = useQueryClient();
  const toast = useToast();

  const done = useMutation({
    mutationFn: (noteId: string) => api(`${BASE}/pings`, { method: "POST", body: { noteId } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["now"] });
      void client.invalidateQueries({ queryKey: keys.pings });
    },
    onError: (error) => toast.error("Couldn't mark that as done", messageOf(error)),
  });

  const pings = data.pings ?? [];
  const more = (data.total ?? pings.length) - pings.length;

  return (
    <section className="panel now-card">
      <div className="panel-head">
        <h2>{card.title}</h2>
      </div>
      <div className="panel-body cr-now">
        {pings.map((ping) => (
          <div className="cr-now-ping" key={ping.noteId}>
            <div className="cr-now-ping-copy">
              <CLink to={creatorHref(ping.creator)} className="cr-feed-name">
                {ping.creator}
              </CLink>
              <NoteBody body={ping.body} />
              <small className="faint">
                {ping.author} · {ago(ping.createdAt)}
              </small>
            </div>
            <button
              type="button"
              className="secondary-button tiny"
              disabled={done.isPending}
              onClick={() => done.mutate(ping.noteId)}
              aria-label={`Mark the note on ${ping.creator} as done`}
            >
              <CheckIcon size={11} /> Done
            </button>
          </div>
        ))}
        <div className="cr-now-foot">
          <span className="muted">{more > 0 ? `${more} more waiting` : ""}</span>
          <CLink to="/creators/notes" className="text-button tiny accent">
            Open notes
          </CLink>
        </div>
      </div>
    </section>
  );
}
